import type { CvRewriteOutput, ResumeDocument } from "@/types/cv";
import { POLISH_AMOUNT } from "@/lib/cv-builder/polish-pricing";
import { getGa4ClientIdFromCookie } from "@/lib/ga-client-id";

type PolishBuilderResumeInput = {
  resume: ResumeDocument;
  outputLanguage: "Indonesian" | "English";
  paymentId: string;
};

type PolishBuilderResumeResponse = {
  jobId: string;
  taskId: string | null;
};

export type BuilderPolishPayment = {
  paymentId: string;
  qrString: string | null;
  amount: number;
};

type SubmitServiceReviewInput = {
  serviceType: "cv_builder_polish";
  paymentId?: string;
  relatedEntityType: "cv_builder_polish_job";
  relatedEntityId: string;
  rating: number;
  comment?: string;
  source: string;
};

type BuilderPolishJobStatus = {
  jobId: string;
  status: "pending" | "processing" | "completed" | "failed";
  error: string | null;
  originalResume: ResumeDocument | null;
  rewrittenResume: ResumeDocument | null;
  completedAt?: string | null;
};

export type BuilderPolishComparison = {
  jobId: string;
  original: ResumeDocument;
  rewritten: ResumeDocument;
  completedAt: string | null;
};

/**
 * The most recent completed polish for the signed-in user, or null when they
 * have never bought one. The job row keeps its own copy of the submitted CV,
 * so the comparison survives later edits to the builder document.
 */
export async function fetchLatestPolishComparison(): Promise<BuilderPolishComparison | null> {
  const response = await fetch("/api/cv-builder/polish", {
    headers: { Accept: "application/json" },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to load the CV polish comparison");
  }

  const payload = (await response.json()) as BuilderPolishJobStatus;

  if (!payload.originalResume || !payload.rewrittenResume) {
    return null;
  }

  return {
    jobId: payload.jobId,
    original: payload.originalResume,
    rewritten: payload.rewrittenResume,
    completedAt: payload.completedAt ?? null,
  };
}

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 120;

const wait = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

export async function createBuilderPolishPayment(): Promise<BuilderPolishPayment> {
  const gaClientId = getGa4ClientIdFromCookie();
  const response = await fetch("/api/cv-builder/polish/payment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(gaClientId ? { gaClientId } : {}),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.error === "string"
        ? payload.error
        : "Failed to create polish payment";
    throw new Error(message);
  }

  return {
    paymentId: String(payload?.paymentId ?? ""),
    qrString: typeof payload?.qrString === "string" ? payload.qrString : null,
    amount: Number(payload?.amount ?? POLISH_AMOUNT),
  };
}

export async function submitServiceReview(input: SubmitServiceReviewInput) {
  const response = await fetch("/api/service-reviews", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.error === "string"
        ? payload.error
        : "Failed to save review";
    throw new Error(message);
  }

  return {
    reviewId: String(payload?.reviewId ?? ""),
  };
}

async function fetchBuilderPolishStatus(
  jobId: string,
): Promise<BuilderPolishJobStatus> {
  const response = await fetch(
    `/api/cv-builder/polish?jobId=${encodeURIComponent(jobId)}`,
    { cache: "no-store" },
  );

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.error === "string"
        ? payload.error
        : "Failed to fetch polish status";
    throw new Error(message);
  }

  return payload as BuilderPolishJobStatus;
}

export async function polishBuilderResume({
  resume,
  outputLanguage,
  paymentId,
}: PolishBuilderResumeInput): Promise<CvRewriteOutput & { jobId: string }> {
  const response = await fetch("/api/cv-builder/polish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resume, outputLanguage, paymentId }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.error === "string"
        ? payload.error
        : "Failed to polish CV";
    throw new Error(message);
  }

  const { jobId } = payload as PolishBuilderResumeResponse;

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    await wait(POLL_INTERVAL_MS);
    const job = await fetchBuilderPolishStatus(jobId);

    if (job.status === "completed" && job.rewrittenResume) {
      return {
        version: "cv-builder-polish",
        generated_at: new Date().toISOString(),
        candidate_name: job.rewrittenResume.personal_identity.name,
        rewritten_cv: job.rewrittenResume,
        jobId: job.jobId,
      };
    }

    if (job.status === "failed") {
      throw new Error(job.error ?? "Failed to polish CV");
    }
  }

  throw new Error(
    "CV polish is taking longer than expected. Please try again.",
  );
}
